import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import polyline from '@mapbox/polyline';
import L from 'leaflet';

const PolylineDecorator = ({ positions, color, minZoom }) => {
    const map = useMap();
    const decoratorRef = useRef(null);

    useEffect(() => {
        if (!positions || positions.length < 2) return;

        const createDecorator = () => {
            if (decoratorRef.current) {
                map.removeLayer(decoratorRef.current);
                decoratorRef.current = null;
            }

            const currentZoom = map.getZoom();
            if (!minZoom || currentZoom < minZoom) {
                return; 
            }

            const decorator = L.polylineDecorator(positions, {
                patterns: [
                    {
                        offset: '5%',
                        repeat: '20%',
                        symbol: L.Symbol.arrowHead({
                            pixelSize: 14,
                            polygon: true, 
                            pathOptions: { 
                                stroke: false,
                                fill: true, 
                                fillColor: color || '#22c55e', 
                                fillOpacity: 1, 
                            },
                        }),
                    },
                ],
            }).addTo(map);

            decoratorRef.current = decorator;
        };

        createDecorator();

        map.on('zoomend', createDecorator);
        map.on('moveend', createDecorator);

        return () => {
            map.off('zoomend', createDecorator);
            map.off('moveend', createDecorator);
            if (decoratorRef.current) {
                map.removeLayer(decoratorRef.current);
            }
        };
    }, [map, JSON.stringify(positions), color, minZoom]); 

    return null;
};

const createNumberedIcon = (index, color = '#ff8c00', customStyle = {}) => {
    const style = {
        background: color,
        color: 'white',
        borderRadius: '50%',
        width: '25px', 
        height: '25px',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontSize: '12px', 
        fontWeight: 'bold',
        ...customStyle,
    };
    const styleString = Object.entries(style)
        .map(([key, val]) => `${key.replace(/[A-Z]/g, m => "-" + m.toLowerCase())}:${val}`)
        .join(';');

    return L.divIcon({
        className: 'custom-numbered-icon',
        html: `<div style="${styleString}">${index + 1}</div>`,
        iconSize: [parseInt(style.width) || 25, parseInt(style.height) || 25],
        iconAnchor: [parseInt(style.width) / 2 || 12, parseInt(style.height) || 25],
    });
};

const fetchRoute = async (waypoints) => {
    const coords = waypoints.map(([lat, lng]) => `${lng},${lat}`).join(';');
    const res = await fetch(
        `https://osrm-map.allaindev.site/route/v1/driving/${coords}?overview=full&geometries=geojson`,
    );
    const data = await res.json();

    if (data.code !== 'Ok') return waypoints;
    return data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
};

const RealPolyline = ({ waypoints, pathOptions }) => {
    const [positions, setPositions] = useState([]);

    useEffect(() => {
        if (waypoints?.length >= 2) {
            fetchRoute(waypoints).then(setPositions);
        }
    }, [JSON.stringify(waypoints)]);

    if (!positions.length) return null;
    return <Polyline positions={positions} pathOptions={pathOptions} />;
};

const MapResizer = () => {
    const map = useMap();
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
        }, 100);
        return () => clearTimeout(timer);
    }, [map]);
    return null;
};

const MapBoundsController = ({ markers, decodedPaths, onMinZoomCalculated }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const bounds = L.latLngBounds([]);

        if (markers && markers.length > 0) {
            markers.forEach(marker => {
                if (marker.position && marker.position[0] && marker.position[1]) {
                    bounds.extend(marker.position);
                }
            });
        }

        if (decodedPaths && decodedPaths.length > 0) {
            decodedPaths.forEach(path => {
                if (path.waypoints && path.waypoints.length > 0) {
                    bounds.extend(path.waypoints);
                }
            });
        }

        if (bounds.isValid()) {
            map.invalidateSize(); 

            map.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 16,
                animate: false,
            });

            const calculatedZoom = map.getBoundsZoom(bounds, false, [50, 50]);
            
            const finalMinZoom = Math.min(calculatedZoom, 15 - 1.5); 

            if (onMinZoomCalculated) {
                onMinZoomCalculated(finalMinZoom);
            }
        }
    }, [map, markers, decodedPaths]);

    return null;
};

// 3. COMPONENT CHA: Quản lý state minZoom chung
const OSMMap = ({ 
    center = [10.762622, 106.660172],
    zoom = 13, 
    markers = [], 
    encodedRoutes = [],
    style = {},
    paths = [],
    realPaths = [], 
}) => {
    // State lưu mức zoom chuẩn được tính từ MapBoundsController
    const [minZoomThreshold, setMinZoomThreshold] = useState(null);

    const decodedPaths = useMemo(() => {
        return encodedRoutes.map((route, index) => {
            try {
                const coordinates = polyline.decode(route.encodedPath);
                return {
                    id: `encoded-${index}`,
                    waypoints: coordinates,
                    color: route.color || '#ff8c00',
                    weight: 4,
                    opacity: 0.8,
                };
            } catch (error) {
                return null;
            }
        }).filter(path => path !== null);
    }, [encodedRoutes]);

    return (
        <div className="map-wrapper" 
            style={{ 
                width: '100%', 
                height: '100vh', 
                position: 'relative',
                ...style, 
            }}
        >
            <MapContainer
                center={center}
                zoom={zoom}
                scrollWheelZoom={true}
                style={{ width: '100%', height: '100%' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapResizer />
                
                {/* Truyền hàm callback để nhận mức zoom tính toán từ Bounds */}
                <MapBoundsController 
                    markers={markers} 
                    decodedPaths={decodedPaths} 
                    onMinZoomCalculated={setMinZoomThreshold}
                />

                {paths.map((path, index) => (
                    <Polyline
                        key={path.id || `path-${index}`}
                        positions={path.positions}
                        pathOptions={{ 
                            color: path.color || '#ff8c00', 
                            weight: path.weight || 4, 
                            dashArray: path.dashArray || null, 
                            opacity: path.opacity || 0.8,
                        }} 
                    />
                ))}
                {realPaths.map((path, index) => (
                    <RealPolyline
                        key={path.id || `real-${index}`}
                        waypoints={path.waypoints}
                        pathOptions={{
                            color: path.color || '#ff8c00',
                            weight: path.weight || 5,
                            dashArray: path.dashArray || null,
                            opacity: path.opacity || 0.9,
                        }}
                    />
                ))}

                {decodedPaths.map((path) => (
                    <React.Fragment key={path.id}>
                        <Polyline
                            positions={path.waypoints}
                            pathOptions={{
                                color: path.color,
                                weight: path.weight,
                                opacity: path.opacity,
                            }}
                        />
                        {/* Truyền minZoom đã được tính toán tự động vào Decorator */}
                        <PolylineDecorator 
                            positions={path.waypoints} 
                            color={path.color} 
                            minZoom={minZoomThreshold}
                        />
                    </React.Fragment>
                ))}

                <MarkerClusterGroup>
                    {markers.map((marker, index) => (
                        <Marker 
                            key={marker.id || index} 
                            position={marker.position} 
                            icon={marker.icon || createNumberedIcon(index, marker.color, marker.iconStyle)}
                        >
                            {marker.content && (
                                <Popup>
                                    <div className="popup-content">{marker.content}</div>
                                </Popup>
                            )}
                        </Marker>
                    ))}
                </MarkerClusterGroup>
            </MapContainer>
        </div>
    );
};

export default OSMMap;